import { IcoHeart, IcoHeartSolid, IcoX } from '@components/icons';
import { Spinner } from '@components/spinner';
import { useState } from 'preact/hooks';
import { Button } from '@components/buttons';
import { useIntl } from 'shared/intl/use-intl';
import { rpx } from 'client/lib/rpx-client';
import { showModalForm } from '@components/modal-form';
import { useTryAsyncData } from 'client/lib/hooks';
import { UserProfileIcon } from '@components/avatars';
import { BaseDialog } from '@components/dialog';
import { URLS } from 'shared/urls';
import { useRouteParams } from '@components/router';

type LikeButtonProps = {
  isLiked: boolean;
  onClick: () => void;
};

type LikeCountProps = {
  commentId: UUID;
  count: number;
  linkToProfilePages: boolean;
};

export function LikeCount({ commentId, count, linkToProfilePages }: LikeCountProps) {
  const intl = useIntl();
  const { courseId, courseSlug } = useRouteParams();

  const text = intl('{count:number} {count | pluralize like likes}', {
    count: count || 0,
  });

  async function showUsers() {
    showModalForm(({ resolve }) => {
      const { data, isLoading } = useTryAsyncData(
        () => rpx.comments.getCommentLikes({ id: commentId }),
        [],
      );

      return (
        <BaseDialog onClose={resolve}>
          <header class="flex justify-between py-4 px-6 border-b">
            <h2 class="text-lg font-semibold">{text}</h2>
            <Button class="text-gray-500 hover:text-gray-700" onClick={resolve}>
              <IcoX class="w-5 h-5" />
            </Button>
          </header>
          {isLoading && <Spinner />}
          <div class="flex flex-col gap-4 p-6 max-h-full overflow-y-auto">
            {data?.map((user) => (
              <Button
                key={user.id}
                class={`flex items-center gap-4 text-gray-600 ${
                  linkToProfilePages ? 'hover:text-indigo-600 cursor-pointer' : 'cursor-text'
                }`}
                href={
                  linkToProfilePages
                    ? URLS.student.memberProfile({
                        course: {
                          id: courseId,
                          title: courseSlug,
                        },
                        user,
                      })
                    : undefined
                }
                onClick={linkToProfilePages ? resolve : undefined}
              >
                <UserProfileIcon user={user} size="size-8" />
                <span>{user.name}</span>
              </Button>
            ))}
          </div>
        </BaseDialog>
      );
    });
  }

  if (!count) {
    return null;
  }

  return (
    <Button class="group ml-1 text-inherit hover:underline" onClick={showUsers}>
      {text}
    </Button>
  );
}

export function LikeButton({ isLiked, onClick }: LikeButtonProps) {
  const [animating, setAnimating] = useState(false);

  return (
    <Button
      class={`${animating ? 'animate-wiggle' : ''} ${
        isLiked ? 'text-red-600 hover:text-red-400' : 'text-red-400 hover:text-red-600'
      } focus:outline-hidden`}
      onClick={() => {
        setAnimating(true);
        onClick();
      }}
      onAnimationEnd={() => setAnimating(false)}
    >
      {isLiked ? <IcoHeartSolid class="w-5 h-5" /> : <IcoHeart class="w-5 h-5" />}
    </Button>
  );
}
